import { useEffect, useState } from 'react';
import { useGameSocket } from '../hooks/useGameSocket';
import './PartyControl.css';

interface TriviaQuestion {
  id: string;
  question: string;
  answer: string;
  explainer?: string;
}

interface TriviaMode {
  currentQuestion: TriviaQuestion | null;
  showAnswer: boolean;
  controllerCount: number;
}

interface TriviaSettings {
  autoAdvance: boolean;
  questionTimeMs: number;
  answerTimeMs: number;
}

const DEFAULT_SETTINGS: TriviaSettings = {
  autoAdvance: false,
  questionTimeMs: 8000,
  answerTimeMs: 5000,
};

export default function PartyControl() {
  useEffect(() => { document.title = 'Gamy Trivia · Controller'; }, []);
  const [triviaMode, setTriviaMode] = useState<TriviaMode>({
    currentQuestion: null,
    showAnswer: false,
    controllerCount: 0,
  });
  const [settings, setSettings] = useState<TriviaSettings>(DEFAULT_SETTINGS);

  const { send, connected } = useGameSocket((msg) => {
    if (msg.type !== 'STATE') return;
    if (msg.triviaMode) setTriviaMode(msg.triviaMode);
    if (msg.triviaSettings) setSettings(msg.triviaSettings);
  });

  // Announce ourselves as a controller on connect / reconnect.
  useEffect(() => {
    if (connected) send({ type: 'TRIVIA_CONTROLLER_HELLO' });
  }, [connected, send]);

  function next() { send({ type: 'TRIVIA_NEXT' }); }
  function reveal() { send({ type: 'TRIVIA_REVEAL' }); }
  function updateSettings(patch: Partial<TriviaSettings>) {
    send({ type: 'TRIVIA_SET_SETTINGS', settings: patch });
  }

  const q = triviaMode.currentQuestion;

  return (
    <div className="pc-page">
      <div className="pc-conn-row">
        <span className={`pc-conn ${connected ? 'pc-conn-ok' : 'pc-conn-bad'}`}>
          {connected ? '● connected' : '○ connecting…'}
        </span>
        <span className="pc-count">{triviaMode.controllerCount} on this game</span>
      </div>

      <div className="pc-card">
        <div className="pc-card-label">Current Question</div>
        <div className="pc-card-text">{q ? q.question : '—'}</div>
        {triviaMode.showAnswer && q && (
          <>
            <div className="pc-card-label pc-card-label-answer">Answer</div>
            <div className="pc-card-answer">{q.answer}</div>
            {q.explainer && (
              <div className="pc-card-explainer">{q.explainer}</div>
            )}
          </>
        )}
      </div>

      <div className="pc-actions">
        <button
          className="pc-btn pc-btn-reveal"
          onClick={reveal}
          disabled={!q || triviaMode.showAnswer}
        >
          {triviaMode.showAnswer ? 'Answer Revealed' : 'Reveal Answer'}
        </button>
        <button className="pc-btn pc-btn-next" onClick={next} disabled={!q}>
          Next Random →
        </button>
      </div>

      <div className="pc-settings">
        <div className="pc-settings-title">Settings</div>

        <label className="pc-row-toggle">
          <input
            type="checkbox"
            checked={settings.autoAdvance}
            onChange={e => updateSettings({ autoAdvance: e.target.checked })}
          />
          <span>Auto-advance</span>
          <span className="pc-row-hint">
            Show question → reveal answer → pause → pick a new random question, on a loop.
          </span>
        </label>

        <div className={`pc-slider-row ${!settings.autoAdvance ? 'pc-slider-row-disabled' : ''}`}>
          <div className="pc-slider-label">
            Show question for <strong>{(settings.questionTimeMs / 1000).toFixed(1)}s</strong> before revealing the answer
          </div>
          <input
            type="range"
            min={2000}
            max={30000}
            step={500}
            value={settings.questionTimeMs}
            onChange={e => updateSettings({ questionTimeMs: Number(e.target.value) })}
            disabled={!settings.autoAdvance}
          />
        </div>

        <div className={`pc-slider-row ${!settings.autoAdvance ? 'pc-slider-row-disabled' : ''}`}>
          <div className="pc-slider-label">
            Then show answer for <strong>{(settings.answerTimeMs / 1000).toFixed(1)}s</strong> before picking the next question
          </div>
          <input
            type="range"
            min={2000}
            max={30000}
            step={500}
            value={settings.answerTimeMs}
            onChange={e => updateSettings({ answerTimeMs: Number(e.target.value) })}
            disabled={!settings.autoAdvance}
          />
        </div>
      </div>

      <div className="pc-footer">Anyone with the QR can control. Settings sync to everyone.</div>
    </div>
  );
}
